import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DailyTotals, FactorAnalysisResult, MetricPeriod } from '@/types'
import { PlanData } from '@/services/analyticsService'
import { calculateFactorAnalysis } from '@/lib/analytics'
import { supabase } from '@/lib/supabase'
import { hasAnyPositive, getChannelPresence } from '@/lib/analytics/subscriptionPresence'

export interface UseFactorAnalysisParams {
  studioId: number
  dailyData: DailyTotals[]
  currentPeriod: MetricPeriod | null
  planData: PlanData | null
}

export interface UseFactorAnalysisReturn {
  data: FactorAnalysisResult | null
  variants: {
    mixed: FactorAnalysisResult | null
    regular: FactorAnalysisResult | null
    subscription: FactorAnalysisResult | null
  }
  canShowSplitViews: boolean
  loading: boolean
  error: string | null
  hasInsufficientData: boolean
}

function projectDailyDataByChannel(rows: DailyTotals[], channel: 'mixed' | 'regular' | 'subscription'): DailyTotals[] {
  if (channel === 'mixed') return rows
  return rows.map((row) => {
    if (channel === 'regular') {
      return { ...row, new_subscriptions_count: 0, new_subscriptions_amount: 0, membership_fees_new_amount: 0 }
    }
    return { ...row, new_sales_count: 0, new_sales_amount: 0 }
  })
}

function projectPlanDataByChannel(planData: PlanData | null, channel: 'mixed' | 'regular' | 'subscription'): PlanData | null {
  if (!planData || channel === 'mixed') return planData
  if (channel === 'regular') {
    return { ...planData, new_subscriptions_count_plan: 0, new_subscriptions_amount_plan: 0, membership_fees_new_plan: 0 }
  }
  return { ...planData, new_sales_count_plan: 0, new_sales_amount_plan: 0 }
}

export function useFactorAnalysis({
  studioId,
  dailyData,
  currentPeriod,
  planData
}: UseFactorAnalysisParams): UseFactorAnalysisReturn {
  const enabled = !!studioId && !!currentPeriod

  const localToday = () => {
    const n = new Date()
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
  }

  const { data: rollingDailyData = [], isLoading, error } = useQuery<DailyTotals[], Error>({
    queryKey: ['factor-analysis-rolling', studioId, localToday()],
    queryFn: async () => {
      const today = localToday()
      const end = new Date(`${today}T00:00:00`)
      const start = new Date(end)
      start.setDate(start.getDate() - 27)
      const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`

      const { data, error } = await supabase
        .from('studio_daily_totals')
        .select(
          'studio_internal_id,metric_date,leads_count,appointments_count,new_visitors_count,new_sales_count,new_sales_amount,membership_fees_new_amount,new_subscriptions_count,new_subscriptions_amount,created_at,updated_at'
        )
        .eq('studio_internal_id', studioId)
        .gte('metric_date', startStr)
        .lte('metric_date', today)
        .order('metric_date', { ascending: true })

      if (error) {
        throw new Error(`Failed to load rolling daily totals: ${error.message}`)
      }

      return (data || []) as DailyTotals[]
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })

  const variants = useMemo(() => {
    if (!currentPeriod) {
      return {
        mixed: null,
        regular: null,
        subscription: null
      }
    }
    return {
      mixed: calculateFactorAnalysis({
        rollingDailyData: projectDailyDataByChannel(rollingDailyData, 'mixed'),
        periodDailyData: projectDailyDataByChannel(dailyData, 'mixed'),
        currentPeriod,
        planData: projectPlanDataByChannel(planData, 'mixed')
      }),
      regular: calculateFactorAnalysis({
        rollingDailyData: projectDailyDataByChannel(rollingDailyData, 'regular'),
        periodDailyData: projectDailyDataByChannel(dailyData, 'regular'),
        currentPeriod,
        planData: projectPlanDataByChannel(planData, 'regular')
      }),
      subscription: calculateFactorAnalysis({
        rollingDailyData: projectDailyDataByChannel(rollingDailyData, 'subscription'),
        periodDailyData: projectDailyDataByChannel(dailyData, 'subscription'),
        currentPeriod,
        planData: projectPlanDataByChannel(planData, 'subscription')
      })
    }
  }, [rollingDailyData, dailyData, currentPeriod, planData])

  const data = variants.mixed || null

  const canShowSplitViews = useMemo(() => {
    if (!currentPeriod) return false
    const regularFactCount = dailyData.reduce((sum, day) => sum + (day.new_sales_count || 0), 0)
    const regularFactAmount = dailyData.reduce((sum, day) => sum + (day.new_sales_amount || 0), 0)
    const subscriptionFactCount = dailyData.reduce((sum, day) => sum + (day.new_subscriptions_count || 0), 0)
    const subscriptionFactAmount = dailyData.reduce(
      (sum, day) => sum + (day.new_subscriptions_amount || 0) + (day.membership_fees_new_amount || 0),
      0
    )

    const factPresence = getChannelPresence(
      [regularFactCount, regularFactAmount],
      [subscriptionFactCount, subscriptionFactAmount]
    )
    const hasRegularPlan = hasAnyPositive([planData?.new_sales_count_plan, planData?.new_sales_amount_plan])
    const hasSubscriptionPlan = hasAnyPositive([
      planData?.new_subscriptions_count_plan,
      planData?.new_subscriptions_amount_plan,
      planData?.membership_fees_new_plan
    ])

    return (factPresence.hasRegular || hasRegularPlan) && (factPresence.hasSubscription || hasSubscriptionPlan)
  }, [currentPeriod, dailyData, planData])

  return {
    data,
    variants,
    canShowSplitViews,
    loading: isLoading,
    error: error ? error.message : null,
    hasInsufficientData: !!data && data.factors.length === 0
  }
}
